import React from 'react';
import { BadgeList } from '#common/badge-list/badge-list.component.tsx';
import { uniqueId } from 'lodash-es';
import {
  ParagraphText,
  StackLayout,
  HeadingText,
  UtilityText,
  Box,
  TextAction,
} from '@leagueplatform/genesis-core';
import { InternalOrExternalLinkButton } from '@leagueplatform/web-common-components';
import { BadgeItem } from '#common/badge-list/badge-item.type.ts';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import { ConditionalLinkRenderer } from '../common/conditional-link-renderer/conditional-link-renderer.component';
import { MasonryDeeplinkAction } from '../../types/masonry-driver-node-actions';
import {
  getTextActionIndicatorIconToken,
  Indicator,
} from '../../utils/icon-mapping';

export interface MasonryLargeCardContentProps {
  heading?: string;
  body: string;
  eyebrow?: string;
  badges?: BadgeItem[];
  linkRefState?: React.RefObject<HTMLAnchorElement>;
  badgeBackgroundColor?: string;
  onClick?: MasonryDeeplinkAction;
  ctaLabel?: string;
  ctaIndicator?: Indicator;
  isCtaLabelledByHeading?: boolean;
}

export const MasonryLargeCardContentRenderer = ({
  eyebrow,
  linkRefState,
  heading,
  body,
  badges,
  badgeBackgroundColor,
  onClick,
  ctaLabel,
  ctaIndicator: _ctaIndicator,
  isCtaLabelledByHeading,
}: MasonryLargeCardContentProps) => {
  const eyebrowId = `lvc-${heading?.replace(/\s+/g, '-')}`;
  const headingId = React.useMemo(
    () => uniqueId('MasonryLargeCardContentRenderer'),
    [],
  );
  const ctaId = `${headingId}-cta`;
  const ctaLabelledById = `${ctaId} ${headingId}`;
  const ctaIndicator = _ctaIndicator
    ? getTextActionIndicatorIconToken(_ctaIndicator)
    : undefined;
  const invokeAction = useInvokeAction();
  const handleOnClick = React.useCallback(
    (event: React.MouseEvent) => {
      if (onClick) {
        invokeAction({
          event,
          ...onClick,
        });
      }
    },
    [invokeAction, onClick],
  );

  return (
    <StackLayout spacing="$half" orientation="vertical">
      {eyebrow && (
        <UtilityText id={eyebrowId} size="eyebrow" as="p">
          {eyebrow}
        </UtilityText>
      )}
      <StackLayout spacing="$oneAndHalf" orientation="vertical">
        <StackLayout spacing="$threeQuarters" orientation="vertical">
          <StackLayout spacing="$quarter" orientation="vertical">
            {heading && (
              <HeadingText
                id={isCtaLabelledByHeading ? headingId : undefined}
                level="3"
                size="sm"
                aria-describedby={eyebrowId}
              >
                <ConditionalLinkRenderer
                  ref={linkRefState}
                  url={ctaLabel ? undefined : onClick?.payload.url}
                  disableDefaultOnClick
                  onClick={handleOnClick}
                >
                  {heading}
                </ConditionalLinkRenderer>
              </HeadingText>
            )}
            {body && (
              <ParagraphText
                size="sm"
                htmlStringOptions={BodyTextHtmlTagOptions}
              >
                {body}
              </ParagraphText>
            )}
          </StackLayout>
          {badges && (
            <Box>
              <BadgeList
                badges={badges}
                backgroundColor={badgeBackgroundColor}
              />
            </Box>
          )}
        </StackLayout>
        {ctaLabel && (
          <Box>
            <TextAction
              aria-labelledby={
                isCtaLabelledByHeading ? ctaLabelledById : undefined
              }
              as={InternalOrExternalLinkButton}
              baseComponent={TextAction}
              disableDefaultOnClick
              href={onClick?.payload.url ?? ''}
              id={isCtaLabelledByHeading ? ctaId : undefined}
              indicator={ctaIndicator}
              onClick={handleOnClick}
              size="medium"
            >
              {ctaLabel}
            </TextAction>
          </Box>
        )}
      </StackLayout>
    </StackLayout>
  );
};
